/**
 * Careers page — every word and every opening from a Google Sheet.
 *
 * sections/careers.liquid renders the frame; this fetches the sheet's two
 * tabs as CSV straight from Google (gviz, which answers with the CORS header
 * a browser needs once the sheet is shared "Anyone with the link"), parses
 * them, and fills the page. "settings" is key / value rows; "jobs" is one row
 * per position with a header row. Column names are forgiving: "Job title",
 * "Salary" and "Qualifications" all land where they should.
 *
 * Nothing from the sheet is ever set as HTML — text nodes only — so a cell
 * cannot inject markup. The last good copy of both tabs is kept in
 * sessionStorage for ten minutes, so a return to the page paints at once.
 *
 * Request 183 (Lucas, 2026-10-20). Applications are an email for now; a form
 * with a resume upload is the next step.
 */
(function () {
  'use strict';

  const TTL = 10 * 60 * 1000;
  const CACHE_PREFIX = 'fmt:careers:';

  /* ---- The sheet ---------------------------------------------------------- */

  function sheetId(link) {
    const text = String(link || '').trim();
    const match = /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/.exec(text);
    if (match) return match[1];
    return /^[a-zA-Z0-9_-]{20,}$/.test(text) ? text : null;
  }

  /**
   * The CSV of one tab, by name — or by gid when the setting is a number.
   * headers=1 says the tab has exactly one header row. Left to guess, Google
   * takes every row above the first number as a header and joins them into
   * one cell, which swallowed the settings tab's first six rows (2026-10-20).
   */
  function csvUrl(id, tab) {
    const which = /^\d+$/.test(tab) ? `gid=${tab}` : `sheet=${encodeURIComponent(tab)}`;
    return `https://docs.google.com/spreadsheets/d/${id}/gviz/tq?tqx=out:csv&headers=1&${which}`;
  }

  /** RFC 4180: quoted fields may hold commas, quotes ("") and newlines. */
  function parseCsv(text) {
    const src = String(text || '').replace(/^﻿/, '');
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < src.length; i += 1) {
      const ch = src[i];
      if (quoted) {
        if (ch === '"') {
          if (src[i + 1] === '"') {
            field += '"';
            i += 1;
          } else {
            quoted = false;
          }
        } else {
          field += ch;
        }
      } else if (ch === '"') {
        quoted = true;
      } else if (ch === ',') {
        row.push(field);
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        if (ch === '\r' && src[i + 1] === '\n') i += 1;
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else {
        field += ch;
      }
    }
    if (field !== '' || row.length) {
      row.push(field);
      rows.push(row);
    }
    return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
  }

  function normaliseKey(value) {
    return String(value || '')
      .replace(/^﻿/, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  const SYNONYMS = {
    job_title: 'title', position: 'title', role: 'title', job: 'title',
    dept: 'department', team: 'department', area: 'department',
    city: 'location', where: 'location', site: 'location',
    job_type: 'type', employment_type: 'type', employment: 'type',
    hours: 'schedule', shift: 'schedule',
    salary: 'pay', wage: 'pay', compensation: 'pay', rate: 'pay', pay_rate: 'pay',
    short_description: 'summary', blurb: 'summary', teaser: 'summary',
    about: 'description', about_the_role: 'description', details: 'description', job_description: 'description',
    duties: 'responsibilities', what_you_will_do: 'responsibilities', what_you_ll_do: 'responsibilities',
    qualifications: 'requirements', what_you_bring: 'requirements', skills: 'requirements',
    perks: 'benefits',
    state: 'status', open: 'status', active: 'status',
    date: 'posted', date_posted: 'posted', posted_on: 'posted',
    link: 'apply_url', apply_link: 'apply_url', url: 'apply_url', application_link: 'apply_url',
    slug: 'id', ref: 'id',
  };

  function toObjects(rows) {
    if (!rows.length) return [];
    const header = rows[0].map((h) => {
      const key = normaliseKey(h);
      return SYNONYMS[key] || key;
    });
    return rows.slice(1).map((r) => {
      const obj = {};
      header.forEach((key, i) => {
        if (key) obj[key] = String(r[i] ?? '').trim();
      });
      return obj;
    });
  }

  /** The wording tab: first column the key, second the value; a header row is skipped. */
  function toSettings(rows) {
    const out = {};
    for (const r of rows) {
      const key = normaliseKey(r[0]);
      if (!key || key === 'key') continue;
      out[key] = String(r[1] ?? '').trim();
    }
    return out;
  }

  function isOpen(status) {
    const s = normaliseKey(status);
    return !s || ['open', 'yes', 'true', 'active', 'live', 'hiring', 'y', '1'].includes(s);
  }

  function slugOf(value) {
    return normaliseKey(value).replace(/_/g, '-').slice(0, 60);
  }

  /** Open rows only, newest first, each with a stable id for its link. */
  function toJobs(rows) {
    const seen = new Set();
    return toObjects(rows)
      .filter((job) => job.title && isOpen(job.status))
      .map((job, index) => {
        let id = slugOf(job.id || job.title) || `job-${index + 1}`;
        while (seen.has(id)) id += '-2';
        seen.add(id);
        return { ...job, id };
      })
      .sort((a, b) => (Date.parse(b.posted) || 0) - (Date.parse(a.posted) || 0));
  }

  /* ---- Building the page (text nodes only) ---------------------------------- */

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null && text !== '') node.textContent = text;
    return node;
  }

  const lines = (text) => String(text || '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);

  /** Paragraphs split on a blank line; a single newline inside one is a break. */
  function paragraphs(text) {
    return String(text || '')
      .split(/\r?\n\s*\r?\n/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => {
        const node = el('p');
        p.split(/\r?\n/).forEach((line, i) => {
          if (i) node.appendChild(el('br'));
          node.appendChild(document.createTextNode(line.trim()));
        });
        return node;
      });
  }

  function group(title, items) {
    if (!items.length) return null;
    const wrap = el('div', 'careers-job__group');
    wrap.appendChild(el('h4', 'careers-job__group-title', title));
    const list = el('ul', 'careers-job__list');
    for (const item of items) list.appendChild(el('li', '', item.replace(/^[-•*]\s*/, '')));
    wrap.appendChild(list);
    return wrap;
  }

  function longDate(value) {
    const t = Date.parse(value);
    if (!t) return value;
    return new Date(t).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }

  function applyHref(job, settings, root) {
    const url = String(job.apply_url || '').trim();
    if (/^https?:\/\//i.test(url) || url.startsWith('mailto:')) return url;
    const email = (settings.apply_email || root.dataset.fallbackEmail || '').trim();
    if (!email) return '';
    return `mailto:${email}?subject=${encodeURIComponent(`Application: ${job.title}`)}`;
  }

  function chevron() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M3 6l5 5 5-5');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.75');
    path.setAttribute('stroke-linecap', 'square');
    svg.appendChild(path);
    return svg;
  }

  function renderJob(job, settings, root) {
    const details = el('details', 'careers-job');
    details.id = `job-${job.id}`;
    details.setAttribute('role', 'listitem');
    details.dataset.careersJob = job.id;

    const summary = el('summary', 'careers-job__summary');
    summary.appendChild(el('h3', 'careers-job__title', job.title));
    const meta = el('ul', 'careers-job__meta');
    for (const key of ['department', 'location', 'type']) {
      if (job[key]) meta.appendChild(el('li', 'careers-job__tag', job[key]));
    }
    if (job.pay) meta.appendChild(el('li', 'careers-job__tag careers-job__tag--pay', job.pay));
    if (meta.childElementCount) summary.appendChild(meta);
    if (job.summary) summary.appendChild(el('p', 'careers-job__lede', job.summary));
    const toggle = el('span', 'careers-job__toggle');
    toggle.setAttribute('aria-hidden', 'true');
    toggle.appendChild(chevron());
    summary.appendChild(toggle);
    details.appendChild(summary);

    const body = el('div', 'careers-job__body');
    for (const p of paragraphs(job.description)) body.appendChild(p);
    for (const [key, label] of [
      ['responsibilities', settings.responsibilities_label || 'What you will do'],
      ['requirements', settings.requirements_label || 'What you bring'],
      ['benefits', settings.benefits_label || 'Benefits'],
    ]) {
      const g = group(label, lines(job[key]));
      if (g) body.appendChild(g);
    }
    const facts = el('dl', 'careers-job__facts');
    for (const [key, label] of [['schedule', 'Schedule'], ['pay', 'Pay'], ['location', 'Location'], ['posted', 'Posted']]) {
      if (!job[key]) continue;
      facts.appendChild(el('dt', '', label));
      facts.appendChild(el('dd', '', key === 'posted' ? longDate(job[key]) : job[key]));
    }
    if (facts.childElementCount) body.appendChild(facts);
    const href = applyHref(job, settings, root);
    if (href) {
      const actions = el('p', 'careers-job__actions');
      const link = el('a', 'button', settings.apply_label || 'Apply for this position');
      link.href = href;
      link.dataset.careersApplyLink = job.id;
      actions.appendChild(link);
      body.appendChild(actions);
    }
    details.appendChild(body);

    // A shareable link per opening: the hash follows what is open.
    details.addEventListener('toggle', () => {
      if (details.open) history.replaceState(null, '', `#${details.id}`);
      else if (location.hash === `#${details.id}`) history.replaceState(null, '', location.pathname + location.search);
    });
    return details;
  }

  function render(root, settings, jobs) {
    // Every piece of wording lands by key; an empty value leaves the fallback.
    for (const node of root.querySelectorAll('[data-careers-text]')) {
      const value = settings[node.dataset.careersText];
      if (value) {
        node.textContent = value;
        node.hidden = false;
      }
    }

    const hero = root.querySelector('[data-careers-hero]');
    const media = root.querySelector('[data-careers-media]');
    const image = root.querySelector('[data-careers-image]');
    const src = String(settings.hero_image || '').trim();
    if (media && image && /^https?:\/\//i.test(src)) {
      image.src = src;
      media.hidden = false;
      hero?.classList.add('careers__hero--with-media');
    }

    const list = root.querySelector('[data-careers-jobs]');
    const empty = root.querySelector('[data-careers-empty]');
    if (list) {
      list.replaceChildren();
      for (const job of jobs) list.appendChild(renderJob(job, settings, root));
      if (empty) {
        if (settings.jobs_empty) empty.textContent = settings.jobs_empty;
        empty.hidden = jobs.length > 0;
      }
    }

    const apply = root.querySelector('[data-careers-apply]');
    if (apply) {
      const email = (settings.apply_email || '').trim();
      const phone = (settings.apply_phone || '').trim();
      const emailLink = apply.querySelector('[data-careers-apply-email]');
      const phoneLink = apply.querySelector('[data-careers-apply-phone]');
      if (emailLink) {
        emailLink.hidden = !email;
        if (email) {
          emailLink.href = `mailto:${email}`;
          emailLink.textContent = email;
        }
      }
      if (phoneLink) {
        phoneLink.hidden = !phone;
        if (phone) {
          phoneLink.href = `tel:${phone.replace(/[^\d+]/g, '')}`;
          phoneLink.textContent = phone;
        }
      }
      apply.hidden = !(settings.apply_heading || settings.apply_text || email);
    }

    root.querySelector('[data-careers-error]')?.setAttribute('hidden', '');
    root.dataset.state = jobs.length ? 'ready' : 'empty';
    openFromHash(root);
  }

  function openFromHash(root) {
    const id = decodeURIComponent(location.hash || '').replace(/^#/, '');
    if (!id) return;
    const wanted = id.replace(/^job-/, '').replace(/[^a-z0-9-]/g, '');
    const target = wanted && root.querySelector(`[data-careers-job="${wanted}"]`);
    if (target && !target.open) {
      target.open = true;
      target.scrollIntoView({ block: 'start' });
    }
  }

  function fail(root) {
    root.dataset.state = 'error';
    const error = root.querySelector('[data-careers-error]');
    if (error) error.hidden = false;
  }

  /* ---- Loading ------------------------------------------------------------- */

  function readCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed.settings === 'string' && typeof parsed.jobs === 'string' ? parsed : null;
    } catch {
      return null;
    }
  }

  function writeCache(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Private mode or a full store: the page still works, just without the cache.
    }
  }

  async function fetchText(url) {
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`${res.status} from ${url}`);
    const text = await res.text();
    // A sheet that is not shared answers 200 with a sign-in page, not CSV.
    if (/^\s*<(!doctype|html)/i.test(text)) throw new Error('not csv');
    return text;
  }

  async function load(root) {
    const id = sheetId(root.dataset.sheet);
    if (!id) {
      fail(root);
      return;
    }
    const key = `${CACHE_PREFIX}${id}`;
    const cached = readCache(key);
    const paint = (settingsCsv, jobsCsv) => render(root, toSettings(parseCsv(settingsCsv)), toJobs(parseCsv(jobsCsv)));
    if (cached) {
      try {
        paint(cached.settings, cached.jobs);
      } catch {
        // A bad cache is just refetched.
      }
      if (Date.now() - (cached.at || 0) < TTL) return;
    }
    try {
      const [settingsCsv, jobsCsv] = await Promise.all([
        fetchText(csvUrl(id, root.dataset.settingsTab || 'settings')),
        fetchText(csvUrl(id, root.dataset.jobsTab || 'jobs')),
      ]);
      paint(settingsCsv, jobsCsv);
      writeCache(key, { at: Date.now(), settings: settingsCsv, jobs: jobsCsv });
    } catch (error) {
      if (!cached) fail(root);
      if (window.console) console.warn('careers: the sheet could not be read —', error.message);
    }
  }

  // Settles once the first load of every careers block on the page is done,
  // however early or late the script runs; the tests await it.
  let settleReady;
  const ready = new Promise((resolve) => { settleReady = resolve; });

  function boot() {
    const roots = [...document.querySelectorAll('[data-careers]')];
    Promise.all(roots.map(load)).then(settleReady, settleReady);
    window.addEventListener('hashchange', () => roots.forEach(openFromHash));
  }

  window.Careers = { sheetId, csvUrl, parseCsv, normaliseKey, toSettings, toJobs, render, load, ready };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
